import React, { useState, useEffect } from 'react';
import { adminApiClient, User } from '../lib/api';
import { DataTable } from './common/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { Search, RefreshCw, UserCircle, Edit2, Trash2, Eye, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { showDeleteConfirmation } from '../lib/sweetAlert';

const DevoteesPage: React.FC = () => {
    const [devotees, setDevotees] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editFormData, setEditFormData] = useState({
        name: '',
        email: '',
        phone: '',
        isActive: true
    });

    useEffect(() => {
        fetchDevotees();
    }, []);

    const fetchDevotees = async () => {
        try {
            setLoading(true);
            const response = await adminApiClient.getUsers({ role: 'devotee' });
            if (response.success && response.data) {
                setDevotees(response.data.users.map((u: any) => ({ ...u, id: u._id })));
            } else {
                toast.error('Failed to fetch devotees');
            }
        } catch (err) {
            toast.error('Error fetching devotees');
        } finally {
            setLoading(false);
        }
    };

    const handleView = (user: User) => {
        setSelectedUser(user);
        setShowViewModal(true);
    };

    const handleEdit = (user: User) => {
        setSelectedUser(user);
        setEditFormData({
            name: user.name || '',
            email: user.email || '',
            phone: user.phone || '',
            isActive: user.isActive ?? true
        });
        setShowEditModal(true);
    };

    const handleSaveEdit = async () => {
        if (!selectedUser) return;

        try {
            const response = await adminApiClient.updateUser(selectedUser._id || selectedUser.id, editFormData);
            if (response.success) {
                toast.success('Devotee updated successfully');
                setShowEditModal(false);
                fetchDevotees();
            } else {
                toast.error('Failed to update devotee');
            }
        } catch (err) {
            toast.error('Error updating devotee');
        }
    };

    const handleDelete = async (userId: string) => {
        const isConfirmed = await showDeleteConfirmation('Delete Devotee?', 'This action cannot be undone.');
        if (!isConfirmed) return;

        try {
            const response = await adminApiClient.deleteUser(userId);
            if (response.success) {
                toast.success('Devotee deleted successfully');
                fetchDevotees();
            } else {
                toast.error('Failed to delete devotee');
            }
        } catch (err) {
            toast.error('Error deleting devotee');
        }
    };

    const handleExport = () => {
        const csv = [
            ['Name', 'Email', 'Phone', 'Status', 'Joined'],
            ...filteredDevotees.map(d => [
                d.name || '',
                d.email || '',
                d.phone || 'N/A',
                d.isActive ? 'Active' : 'Inactive',
                new Date(d.createdAt).toLocaleDateString()
            ])
        ].map(row => row.join(',')).join('\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `devotees-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        toast.success('Exported successfully');
    };

    const columns: ColumnDef<User>[] = [
        {
            accessorKey: 'name',
            header: 'Name',
            cell: (info) => (
                <div className="flex items-center">
                    <UserCircle className="w-5 h-5 mr-2 text-gray-400" />
                    <span className="font-medium">{info.getValue() as string}</span>
                </div>
            )
        },
        {
            accessorKey: 'email',
            header: 'Email',
        },
        {
            accessorKey: 'phone',
            header: 'Phone',
            cell: (info) => info.getValue() || 'N/A'
        },
        {
            accessorKey: 'isActive',
            header: 'Status',
            cell: (info) => (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${info.getValue() ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                    {info.getValue() ? 'Active' : 'Inactive'}
                </span>
            )
        },
        {
            accessorKey: 'createdAt',
            header: 'Joined',
            cell: (info) => new Date(info.getValue() as string).toLocaleDateString()
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: (info) => (
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => handleView(info.row.original)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                        title="View Details"
                    >
                        <Eye size={16} />
                    </button>
                    <button
                        onClick={() => handleEdit(info.row.original)}
                        className="p-1.5 text-gray-600 hover:bg-gray-50 rounded-lg transition"
                        title="Edit"
                    >
                        <Edit2 size={16} />
                    </button>
                    <button
                        onClick={() => handleDelete(info.row.original._id || info.row.original.id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="Delete"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            )
        }
    ];

    const filteredDevotees = devotees.filter(d =>
        d.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Devotees</h1>
                    <p className="text-sm text-gray-500">Manage devotee accounts</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleExport}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white hover:bg-green-700 rounded-lg transition"
                    >
                        <Download size={18} />
                        Export
                    </button>
                    <button
                        onClick={fetchDevotees}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
                    >
                        <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                </div>
            </div>

            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6">
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                        type="text"
                        placeholder="Search devotees..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                    />
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <DataTable
                    data={filteredDevotees}
                    columns={columns}
                    pagination={undefined}
                    sorting={undefined}
                    enableClientSidePagination={true}
                    loading={loading}
                />
            </div>

            {/* View Modal */}
            {showViewModal && selectedUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-gray-900">Devotee Details</h2>
                            <button onClick={() => setShowViewModal(false)} className="text-gray-400 hover:bg-gray-100 p-1 rounded-full">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="text-sm font-medium text-gray-500">Name</label>
                                <p className="text-gray-900 mt-1">{selectedUser.name}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-500">Email</label>
                                <p className="text-gray-900 mt-1">{selectedUser.email}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-500">Phone</label>
                                <p className="text-gray-900 mt-1">{selectedUser.phone || 'N/A'}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-500">Status</label>
                                <p className="mt-1">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${selectedUser.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                        {selectedUser.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-500">Joined</label>
                                <p className="text-gray-900 mt-1">{new Date(selectedUser.createdAt).toLocaleDateString()}</p>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end">
                            <button onClick={() => setShowViewModal(false)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-gray-900">Edit Devotee</h2>
                            <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:bg-gray-100 p-1 rounded-full">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                <input
                                    type="text"
                                    value={editFormData.name}
                                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input
                                    type="email"
                                    value={editFormData.email}
                                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                <input
                                    type="tel"
                                    value={editFormData.phone}
                                    onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                <select
                                    value={editFormData.isActive ? 'true' : 'false'}
                                    onChange={(e) => setEditFormData({ ...editFormData, isActive: e.target.value === 'true' })}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                                >
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </select>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button onClick={() => setShowEditModal(false)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg">
                                Cancel
                            </button>
                            <button onClick={handleSaveEdit} className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg">
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DevoteesPage;
